export const WINDOW_LAYOUT_VERSION = 1 as const;

const DEFAULT_GUTTER = 4;
const DEFAULT_TASKBAR_HEIGHT = 30;
const DEFAULT_TITLE_BAR_HEIGHT = 28;
const DEFAULT_MIN_WIDTH = 160;
const DEFAULT_MIN_HEIGHT = 120;

type VisibleWindowMode = "open" | "maximized";

export type WindowMode = VisibleWindowMode | "closed" | "minimized";

export interface WindowBounds {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface WindowSize {
	width: number;
	height: number;
}

export interface WindowInsets {
	top: number;
	right: number;
	bottom: number;
	left: number;
}

export interface WindowViewport {
	width: number;
	height: number;
	taskbarHeight?: number;
	gutter?: number;
	insets?: Partial<WindowInsets>;
}

export interface WindowDefinition<WindowId extends string = string> {
	id: WindowId;
	defaultBounds: WindowBounds;
	defaultMode?: "closed" | VisibleWindowMode;
	minimumSize?: Partial<WindowSize>;
	maximizable?: boolean;
	responsivePlacement?: Partial<
		Record<"handheld" | "compact" | "wide", WindowPlacement>
	>;
	titleBarHeight?: number;
}

export interface WindowPlacement {
	x: number;
	y: number;
}

export interface WindowEntry<WindowId extends string = string> {
	id: WindowId;
	mode: WindowMode;
	restoreMode: VisibleWindowMode;
	bounds: WindowBounds;
	restoreBounds: WindowBounds | null;
}

export interface WindowLayoutState<WindowId extends string = string> {
	version: typeof WINDOW_LAYOUT_VERSION;
	windows: Record<WindowId, WindowEntry<WindowId>>;
	zOrder: WindowId[];
	activeWindowId: WindowId | null;
}

export interface WindowDragDelta {
	dx: number;
	dy: number;
}

type UnknownRecord = Record<string, unknown>;

type WindowIdFromDefinitions<TDefinitions extends readonly WindowDefinition[]> =
	TDefinitions[number]["id"];

interface ViewportBounds extends WindowBounds {}

interface InternalViewport {
	width: number;
	height: number;
	gutter: number;
	taskbarHeight: number;
	insets: WindowInsets;
	available: ViewportBounds;
}

export function createWindowLayout<
	const TDefinitions extends readonly WindowDefinition[],
>(
	definitions: TDefinitions,
	viewport: WindowViewport,
): WindowLayoutState<WindowIdFromDefinitions<TDefinitions>> {
	type WindowId = WindowIdFromDefinitions<TDefinitions>;

	const windows = {} as Record<WindowId, WindowEntry<WindowId>>;
	for (const definition of definitions) {
		const entry = buildDefaultEntry(
			definition as WindowDefinition<WindowId>,
			viewport,
		);
		windows[entry.id] = entry;
	}

	const normalized = normalizeState<WindowId>(
		{
			version: WINDOW_LAYOUT_VERSION,
			windows,
			zOrder: collectVisibleIds(windows),
			activeWindowId: null,
		},
		definitions as readonly WindowDefinition<WindowId>[],
		viewport,
	);

	return normalized;
}

export function restoreWindowLayout<
	const TDefinitions extends readonly WindowDefinition[],
>(
	definitions: TDefinitions,
	viewport: WindowViewport,
	candidate: unknown,
): WindowLayoutState<WindowIdFromDefinitions<TDefinitions>> {
	type WindowId = WindowIdFromDefinitions<TDefinitions>;
	const defaults = createWindowLayout(definitions, viewport);

	if (!isRecord(candidate) || candidate.version !== WINDOW_LAYOUT_VERSION) {
		return defaults;
	}

	if (!isRecord(candidate.windows) || !Array.isArray(candidate.zOrder)) {
		return defaults;
	}

	const windows = { ...defaults.windows } as Record<
		WindowId,
		WindowEntry<WindowId>
	>;
	for (const definition of definitions as readonly WindowDefinition<WindowId>[]) {
		const rawEntry = candidate.windows[definition.id];
		const nextEntry = parseWindowEntry(rawEntry, definition, viewport);
		if (nextEntry) {
			windows[definition.id] = nextEntry;
		}
	}

	const activeWindowId =
		typeof candidate.activeWindowId === "string"
			? (candidate.activeWindowId as WindowId)
			: null;

	return normalizeState<WindowId>(
		{
			version: WINDOW_LAYOUT_VERSION,
			windows,
			zOrder: candidate.zOrder.filter(
				(value): value is WindowId => typeof value === "string",
			),
			activeWindowId,
		},
		definitions as readonly WindowDefinition<WindowId>[],
		viewport,
	);
}

export function serializeWindowLayout<WindowId extends string>(
	state: WindowLayoutState<WindowId>,
): WindowLayoutState<WindowId> {
	const windows = {} as Record<WindowId, WindowEntry<WindowId>>;
	for (const windowId of Object.keys(state.windows) as WindowId[]) {
		const entry = state.windows[windowId];
		windows[windowId] = {
			...entry,
			bounds: { ...entry.bounds },
			restoreBounds: entry.restoreBounds ? { ...entry.restoreBounds } : null,
		};
	}

	return {
		version: WINDOW_LAYOUT_VERSION,
		windows,
		zOrder: [...state.zOrder],
		activeWindowId: state.activeWindowId,
	};
}

export function openWindow<
	const TDefinitions extends readonly WindowDefinition[],
>(
	state: WindowLayoutState<WindowIdFromDefinitions<TDefinitions>>,
	definitions: TDefinitions,
	viewport: WindowViewport,
	windowId: WindowIdFromDefinitions<TDefinitions>,
): WindowLayoutState<WindowIdFromDefinitions<TDefinitions>> {
	return withWindow(
		state,
		definitions,
		viewport,
		windowId,
		(entry, definition, normalizedViewport) => {
			if (entry.mode === "open" || entry.mode === "maximized") {
				return focusWindow(state, windowId);
			}

			const restored = restoreEntry(entry, definition, normalizedViewport);
			return finalizeWindowUpdate(
				state,
				definitions,
				viewport,
				restored.id,
				restored,
				{ bringToFront: true },
			);
		},
	);
}

export function focusWindow<WindowId extends string>(
	state: WindowLayoutState<WindowId>,
	windowId: WindowId,
): WindowLayoutState<WindowId> {
	const entry = state.windows[windowId];
	if (!entry || entry.mode === "closed" || entry.mode === "minimized") {
		return state;
	}

	const zOrder = [...state.zOrder.filter((id) => id !== windowId), windowId];
	return {
		...state,
		zOrder,
		activeWindowId: windowId,
	};
}

export function dragWindow<
	const TDefinitions extends readonly WindowDefinition[],
>(
	state: WindowLayoutState<WindowIdFromDefinitions<TDefinitions>>,
	definitions: TDefinitions,
	viewport: WindowViewport,
	windowId: WindowIdFromDefinitions<TDefinitions>,
	delta: WindowDragDelta,
): WindowLayoutState<WindowIdFromDefinitions<TDefinitions>> {
	return withWindow(
		state,
		definitions,
		viewport,
		windowId,
		(entry, definition, normalizedViewport) => {
			if (entry.mode !== "open") {
				return state;
			}

			const dragged = clampWindowBounds(
				{
					...entry.bounds,
					x: entry.bounds.x + delta.dx,
					y: entry.bounds.y + delta.dy,
				},
				definition,
				normalizedViewport,
			);

			return finalizeWindowUpdate(
				state,
				definitions,
				viewport,
				entry.id,
				{
					...entry,
					bounds: dragged,
				},
				{ bringToFront: true },
			);
		},
	);
}

export function minimizeWindow<
	const TDefinitions extends readonly WindowDefinition[],
>(
	state: WindowLayoutState<WindowIdFromDefinitions<TDefinitions>>,
	definitions: TDefinitions,
	viewport: WindowViewport,
	windowId: WindowIdFromDefinitions<TDefinitions>,
): WindowLayoutState<WindowIdFromDefinitions<TDefinitions>> {
	return withWindow(state, definitions, viewport, windowId, (entry) => {
		if (entry.mode === "closed" || entry.mode === "minimized") {
			return state;
		}

		const nextEntry: WindowEntry<typeof windowId> = {
			...entry,
			mode: "minimized",
			restoreMode: entry.mode === "maximized" ? "maximized" : "open",
		};

		return finalizeWindowUpdate(
			state,
			definitions,
			viewport,
			entry.id,
			nextEntry,
		);
	});
}

export function restoreWindow<
	const TDefinitions extends readonly WindowDefinition[],
>(
	state: WindowLayoutState<WindowIdFromDefinitions<TDefinitions>>,
	definitions: TDefinitions,
	viewport: WindowViewport,
	windowId: WindowIdFromDefinitions<TDefinitions>,
): WindowLayoutState<WindowIdFromDefinitions<TDefinitions>> {
	return withWindow(
		state,
		definitions,
		viewport,
		windowId,
		(entry, definition, normalizedViewport) => {
			if (entry.mode === "closed") {
				return state;
			}

			if (entry.mode === "minimized") {
				const restored = restoreEntry(entry, definition, normalizedViewport);
				return finalizeWindowUpdate(
					state,
					definitions,
					viewport,
					restored.id,
					restored,
					{ bringToFront: true },
				);
			}

			if (entry.mode !== "maximized") {
				return state;
			}

			const fallbackBounds = clampWindowBounds(
				entry.restoreBounds ?? entry.bounds,
				definition,
				normalizedViewport,
			);

			return finalizeWindowUpdate(
				state,
				definitions,
				viewport,
				entry.id,
				{
					...entry,
					mode: "open",
					restoreMode: "open",
					bounds: fallbackBounds,
					restoreBounds: null,
				},
				{ bringToFront: true },
			);
		},
	);
}

export function maximizeWindow<
	const TDefinitions extends readonly WindowDefinition[],
>(
	state: WindowLayoutState<WindowIdFromDefinitions<TDefinitions>>,
	definitions: TDefinitions,
	viewport: WindowViewport,
	windowId: WindowIdFromDefinitions<TDefinitions>,
): WindowLayoutState<WindowIdFromDefinitions<TDefinitions>> {
	return withWindow(
		state,
		definitions,
		viewport,
		windowId,
		(entry, definition, normalizedViewport) => {
			if (entry.mode === "closed" || definition.maximizable === false) {
				return state;
			}

			if (entry.mode === "maximized") {
				return focusWindow(state, windowId);
			}

			const baseBounds = clampWindowBounds(
				entry.bounds,
				definition,
				normalizedViewport,
			);

			return finalizeWindowUpdate(
				state,
				definitions,
				viewport,
				entry.id,
				{
					...entry,
					mode: "maximized",
					restoreMode: "open",
					bounds: maximizeWindowBounds(normalizedViewport),
					restoreBounds: baseBounds,
				},
				{ bringToFront: true },
			);
		},
	);
}

export function closeWindow<
	const TDefinitions extends readonly WindowDefinition[],
>(
	state: WindowLayoutState<WindowIdFromDefinitions<TDefinitions>>,
	definitions: TDefinitions,
	viewport: WindowViewport,
	windowId: WindowIdFromDefinitions<TDefinitions>,
): WindowLayoutState<WindowIdFromDefinitions<TDefinitions>> {
	return withWindow(state, definitions, viewport, windowId, (entry) => {
		if (entry.mode === "closed") {
			return state;
		}

		return finalizeWindowUpdate(state, definitions, viewport, entry.id, {
			...entry,
			mode: "closed",
		});
	});
}

export function reflowWindowLayout<
	const TDefinitions extends readonly WindowDefinition[],
>(
	state: WindowLayoutState<WindowIdFromDefinitions<TDefinitions>>,
	definitions: TDefinitions,
	viewport: WindowViewport,
): WindowLayoutState<WindowIdFromDefinitions<TDefinitions>> {
	type WindowId = WindowIdFromDefinitions<TDefinitions>;
	const typedDefinitions = definitions as readonly WindowDefinition<WindowId>[];
	const normalizedViewport = normalizeViewport(viewport);
	const windows = {} as Record<WindowId, WindowEntry<WindowId>>;

	for (const definition of typedDefinitions) {
		const current = state.windows[definition.id];
		if (!current) continue;
		const responsiveBounds = getResponsiveDefaultBounds(
			definition,
			normalizedViewport,
		);
		const remembersRestoreBounds =
			current.mode === "maximized" ||
			current.restoreMode === "maximized" ||
			current.restoreBounds !== null;

		windows[definition.id] = {
			...current,
			bounds:
				current.mode === "maximized"
					? maximizeWindowBounds(normalizedViewport)
					: responsiveBounds,
			restoreBounds: remembersRestoreBounds ? responsiveBounds : null,
		};
	}

	return normalizeState({ ...state, windows }, typedDefinitions, viewport);
}

export function resetWindowLayout<
	const TDefinitions extends readonly WindowDefinition[],
>(
	definitions: TDefinitions,
	viewport: WindowViewport,
): WindowLayoutState<WindowIdFromDefinitions<TDefinitions>> {
	return createWindowLayout(definitions, viewport);
}

function finalizeWindowUpdate<WindowId extends string>(
	state: WindowLayoutState<WindowId>,
	definitions: readonly WindowDefinition<WindowId>[],
	viewport: WindowViewport,
	windowId: WindowId,
	nextEntry: WindowEntry<WindowId>,
	options: { bringToFront?: boolean } = {},
): WindowLayoutState<WindowId> {
	const windows = {
		...state.windows,
		[windowId]: nextEntry,
	};

	const zOrder =
		nextEntry.mode === "closed"
			? state.zOrder.filter((id) => id !== windowId)
			: options.bringToFront
				? [...state.zOrder.filter((id) => id !== windowId), windowId]
				: state.zOrder;

	const activeWindowId =
		nextEntry.mode === "open" || nextEntry.mode === "maximized"
			? windowId
			: state.activeWindowId === windowId
				? null
				: state.activeWindowId;

	return normalizeState(
		{
			version: WINDOW_LAYOUT_VERSION,
			windows,
			zOrder,
			activeWindowId,
		},
		definitions,
		viewport,
	);
}

function withWindow<WindowId extends string>(
	state: WindowLayoutState<WindowId>,
	definitions: readonly WindowDefinition<WindowId>[],
	viewport: WindowViewport,
	windowId: WindowId,
	updater: (
		entry: WindowEntry<WindowId>,
		definition: WindowDefinition<WindowId>,
		normalizedViewport: InternalViewport,
	) => WindowLayoutState<WindowId>,
): WindowLayoutState<WindowId> {
	const entry = state.windows[windowId];
	const definition = definitions.find((candidate) => candidate.id === windowId);
	if (!entry || !definition) {
		return state;
	}

	return updater(entry, definition, normalizeViewport(viewport));
}

function normalizeState<WindowId extends string>(
	state: WindowLayoutState<WindowId>,
	definitions: readonly WindowDefinition<WindowId>[],
	viewport: WindowViewport,
): WindowLayoutState<WindowId> {
	const normalizedViewport = normalizeViewport(viewport);
	const windows = {} as Record<WindowId, WindowEntry<WindowId>>;

	for (const definition of definitions) {
		const defaultEntry = buildDefaultEntry(definition, normalizedViewport);
		const parsed = normalizeWindowEntry(
			state.windows[definition.id],
			definition,
			normalizedViewport,
		);
		windows[definition.id] = parsed ?? defaultEntry;
	}

	const zOrder = normalizeZOrder(state.zOrder, definitions, windows);
	const activeWindowId = normalizeActiveWindowId(
		state.activeWindowId,
		zOrder,
		windows,
	);

	return {
		version: WINDOW_LAYOUT_VERSION,
		windows,
		zOrder,
		activeWindowId,
	};
}

function normalizeZOrder<WindowId extends string>(
	zOrder: readonly WindowId[],
	definitions: readonly WindowDefinition<WindowId>[],
	windows: Record<WindowId, WindowEntry<WindowId>>,
): WindowId[] {
	const knownIds = new Set(definitions.map((definition) => definition.id));
	const seen = new Set<WindowId>();
	const normalized: WindowId[] = [];

	for (const windowId of zOrder) {
		if (
			!knownIds.has(windowId) ||
			seen.has(windowId) ||
			windows[windowId].mode === "closed"
		) {
			continue;
		}

		seen.add(windowId);
		normalized.push(windowId);
	}

	for (const definition of definitions) {
		if (seen.has(definition.id) || windows[definition.id].mode === "closed") {
			continue;
		}

		seen.add(definition.id);
		normalized.push(definition.id);
	}

	return normalized;
}

function normalizeActiveWindowId<WindowId extends string>(
	activeWindowId: WindowId | null,
	zOrder: readonly WindowId[],
	windows: Record<WindowId, WindowEntry<WindowId>>,
): WindowId | null {
	if (activeWindowId) {
		const activeEntry = windows[activeWindowId];
		if (
			activeEntry &&
			(activeEntry.mode === "open" || activeEntry.mode === "maximized")
		) {
			return activeWindowId;
		}
	}

	for (let index = zOrder.length - 1; index >= 0; index -= 1) {
		const windowId = zOrder[index];
		if (!windowId) continue;
		const entry = windows[windowId];
		if (entry.mode === "open" || entry.mode === "maximized") {
			return entry.id;
		}
	}

	return null;
}

function collectVisibleIds<WindowId extends string>(
	windows: Record<WindowId, WindowEntry<WindowId>>,
): WindowId[] {
	return (Object.keys(windows) as WindowId[]).filter(
		(windowId) => windows[windowId].mode !== "closed",
	);
}

function buildDefaultEntry<WindowId extends string>(
	definition: WindowDefinition<WindowId>,
	viewport: WindowViewport | InternalViewport,
): WindowEntry<WindowId> {
	const normalizedViewport = isInternalViewport(viewport)
		? viewport
		: normalizeViewport(viewport);
	const baseBounds = getResponsiveDefaultBounds(definition, normalizedViewport);
	const defaultMode = normalizeDefaultMode(definition);

	if (defaultMode === "maximized" && definition.maximizable !== false) {
		return {
			id: definition.id,
			mode: "maximized",
			restoreMode: "open",
			bounds: maximizeWindowBounds(normalizedViewport),
			restoreBounds: baseBounds,
		};
	}

	return {
		id: definition.id,
		mode: defaultMode,
		restoreMode: "open",
		bounds: baseBounds,
		restoreBounds: null,
	};
}

function parseWindowEntry<WindowId extends string>(
	candidate: unknown,
	definition: WindowDefinition<WindowId>,
	viewport: WindowViewport,
): WindowEntry<WindowId> | null {
	if (!isRecord(candidate)) {
		return null;
	}

	return normalizeWindowEntry(
		candidate,
		definition,
		normalizeViewport(viewport),
	);
}

function normalizeWindowEntry<WindowId extends string>(
	candidate: unknown,
	definition: WindowDefinition<WindowId>,
	viewport: InternalViewport,
): WindowEntry<WindowId> | null {
	if (!isRecord(candidate)) {
		return null;
	}

	const mode = normalizeMode(candidate.mode, definition.maximizable !== false);
	const restoreMode = normalizeVisibleMode(
		candidate.restoreMode,
		definition.maximizable !== false,
	);
	const bounds = parseBounds(candidate.bounds);
	const restoreBounds = parseBounds(candidate.restoreBounds);

	if (!mode || !restoreMode || !bounds) {
		return null;
	}

	const clampedBounds = clampWindowBounds(bounds, definition, viewport);
	const clampedRestoreBounds = restoreBounds
		? clampWindowBounds(restoreBounds, definition, viewport)
		: null;

	if (mode === "maximized") {
		return {
			id: definition.id,
			mode,
			restoreMode: "open",
			bounds: maximizeWindowBounds(viewport),
			restoreBounds:
				clampedRestoreBounds ??
				clampWindowBounds(definition.defaultBounds, definition, viewport),
		};
	}

	return {
		id: definition.id,
		mode,
		restoreMode,
		bounds: clampedBounds,
		restoreBounds:
			restoreMode === "maximized"
				? (clampedRestoreBounds ??
					clampWindowBounds(definition.defaultBounds, definition, viewport))
				: clampedRestoreBounds,
	};
}

function restoreEntry<WindowId extends string>(
	entry: WindowEntry<WindowId>,
	definition: WindowDefinition<WindowId>,
	viewport: InternalViewport,
): WindowEntry<WindowId> {
	if (entry.restoreMode === "maximized" && definition.maximizable !== false) {
		return {
			...entry,
			mode: "maximized",
			bounds: maximizeWindowBounds(viewport),
			restoreBounds: clampWindowBounds(
				entry.restoreBounds ?? definition.defaultBounds,
				definition,
				viewport,
			),
		};
	}

	return {
		...entry,
		mode: "open",
		restoreMode: "open",
		bounds: clampWindowBounds(entry.bounds, definition, viewport),
		restoreBounds: entry.restoreBounds
			? clampWindowBounds(entry.restoreBounds, definition, viewport)
			: null,
	};
}

function maximizeWindowBounds(viewport: InternalViewport): WindowBounds {
	return { ...viewport.available };
}

function getResponsiveDefaultBounds<WindowId extends string>(
	definition: WindowDefinition<WindowId>,
	viewport: InternalViewport,
): WindowBounds {
	const bounds = clampWindowBounds(
		definition.defaultBounds,
		definition,
		viewport,
	);
	const mode =
		viewport.width < 600
			? "handheld"
			: viewport.width < 1024
				? "compact"
				: "wide";
	const placement = definition.responsivePlacement?.[mode];
	if (!placement) return bounds;

	const freeWidth = Math.max(0, viewport.available.width - bounds.width);
	const freeHeight = Math.max(0, viewport.available.height - bounds.height);

	return {
		...bounds,
		x:
			viewport.available.x +
			Math.round(freeWidth * clampNumber(placement.x, 0, 1)),
		y:
			viewport.available.y +
			Math.round(freeHeight * clampNumber(placement.y, 0, 1)),
	};
}

function clampWindowBounds<WindowId extends string>(
	bounds: WindowBounds,
	definition: WindowDefinition<WindowId>,
	viewport: InternalViewport,
): WindowBounds {
	const minimumSize = getMinimumSize(definition, viewport.available);
	const width = clampNumber(
		bounds.width,
		minimumSize.width,
		viewport.available.width,
	);
	const height = clampNumber(
		bounds.height,
		minimumSize.height,
		viewport.available.height,
	);
	const maxX = viewport.available.x + viewport.available.width - width;
	const maxY = viewport.available.y + viewport.available.height - height;

	return {
		x: clampNumber(bounds.x, viewport.available.x, maxX),
		y: clampNumber(bounds.y, viewport.available.y, maxY),
		width,
		height,
	};
}

function getMinimumSize<WindowId extends string>(
	definition: WindowDefinition<WindowId>,
	available: WindowBounds,
): WindowSize {
	const titleBarHeight = definition.titleBarHeight ?? DEFAULT_TITLE_BAR_HEIGHT;
	const requestedWidth = definition.minimumSize?.width ?? DEFAULT_MIN_WIDTH;
	const requestedHeight =
		definition.minimumSize?.height ??
		Math.max(DEFAULT_MIN_HEIGHT, titleBarHeight);

	return {
		width: clampLowerBound(Math.min(requestedWidth, available.width)),
		height: clampLowerBound(Math.min(requestedHeight, available.height)),
	};
}

function normalizeViewport(viewport: WindowViewport): InternalViewport {
	const gutter = clampLowerBound(viewport.gutter ?? DEFAULT_GUTTER);
	const taskbarHeight = clampLowerBound(
		viewport.taskbarHeight ?? DEFAULT_TASKBAR_HEIGHT,
	);
	const insets: WindowInsets = {
		top: clampLowerBound(viewport.insets?.top ?? 0),
		right: clampLowerBound(viewport.insets?.right ?? 0),
		bottom: clampLowerBound(viewport.insets?.bottom ?? 0),
		left: clampLowerBound(viewport.insets?.left ?? 0),
	};

	const availableWidth = Math.max(
		1,
		viewport.width - insets.left - insets.right - gutter * 2,
	);
	const availableHeight = Math.max(
		1,
		viewport.height - insets.top - insets.bottom - taskbarHeight - gutter * 2,
	);

	return {
		width: viewport.width,
		height: viewport.height,
		gutter,
		taskbarHeight,
		insets,
		available: {
			x: insets.left + gutter,
			y: insets.top + gutter,
			width: availableWidth,
			height: availableHeight,
		},
	};
}

function normalizeDefaultMode<WindowId extends string>(
	definition: WindowDefinition<WindowId>,
): WindowMode {
	const mode = definition.defaultMode ?? "closed";
	if (mode === "maximized" && definition.maximizable === false) {
		return "open";
	}

	return mode;
}

function normalizeMode(
	candidate: unknown,
	maximizable: boolean,
): WindowMode | null {
	if (
		candidate !== "closed" &&
		candidate !== "minimized" &&
		candidate !== "open" &&
		candidate !== "maximized"
	) {
		return null;
	}

	if (candidate === "maximized" && !maximizable) {
		return "open";
	}

	return candidate;
}

function normalizeVisibleMode(
	candidate: unknown,
	maximizable: boolean,
): VisibleWindowMode | null {
	if (candidate === "open") {
		return candidate;
	}

	if (candidate === "maximized") {
		return maximizable ? candidate : "open";
	}

	return null;
}

function parseBounds(candidate: unknown): WindowBounds | null {
	if (!isRecord(candidate)) {
		return null;
	}

	const x = parseFiniteNumber(candidate.x);
	const y = parseFiniteNumber(candidate.y);
	const width = parseFiniteNumber(candidate.width);
	const height = parseFiniteNumber(candidate.height);

	if (x === null || y === null || width === null || height === null) {
		return null;
	}

	return { x, y, width, height };
}

function parseFiniteNumber(candidate: unknown): number | null {
	return typeof candidate === "number" && Number.isFinite(candidate)
		? candidate
		: null;
}

function clampNumber(value: number, minimum: number, maximum: number): number {
	if (minimum >= maximum) {
		return minimum;
	}

	return Math.min(Math.max(value, minimum), maximum);
}

function clampLowerBound(value: number): number {
	return Math.max(0, Math.floor(value));
}

function isRecord(candidate: unknown): candidate is UnknownRecord {
	return typeof candidate === "object" && candidate !== null;
}

function isInternalViewport(
	candidate: WindowViewport | InternalViewport,
): candidate is InternalViewport {
	return "available" in candidate;
}
